<template>
	<component
		:is="tag"
		:to="to"
		:href="href"
		class="btn"
		tabindex="0"
		:class="{
			[`btn--${className}`]: className,
			'btn--small': size === 'small',
			'btn--block': block,
			'btn--square': square,
			'is-disabled': disabled,
			'is-highlighted': highlighted,
			'is-active': active,
			[modifier]: modifier
		}"
		v-bind="$attrs"
		v-tooltip.top="tooltip"
		@click="$emit('onClick', $event)"
	>
		<slot />
	</component>
</template>

<script>
export default {
	/**
	 * Name
	 *
	 * Deliberately NOT 'Button': this component renders
	 * <component :is="tag"> where tag is the string 'button', and Vue 3
	 * resolves that against registered component names before falling back to
	 * a native element. With name: 'Button' it resolved to itself and
	 * recursed until the stack blew. Templates are unaffected - they resolve
	 * <Button> through the local `components` registration, not through this.
	 */
	name: 'BaseButton',

	/**
	 * Props
	 */
	props: {
		// floating-vue's directive has to sit on a DOM element, not on a
		// component, so tooltips on <Button> come through this prop and are
		// applied to the rendered element below.
		tooltip: {
			type: String,
			default: ''
		},
		to: {
			type: String,
			default: null
		},
		href: {
			type: String,
			default: null
		},
		modifier: {
			type: String,
			default: ''
		},
		className: {
			type: String,
			default: 'primary',
			validator: function(value) {
				// The value must match one of these strings
				return ['primary', 'secondary', 'tertiary'].indexOf(value) !== -1;
			}
		},
		block: {
			type: Boolean,
			default: false
		},
		size: {
			type: String,
			default: '',
			validator: function(value) {
				// The value must match one of these strings
				return ['', 'small'].indexOf(value) !== -1;
			}
		},
		disabled: {
			type: Boolean,
			default: false
		},
		highlighted: {
			type: Boolean,
			default: false
		},
		active: {
			type: Boolean,
			default: false
		},
		square: {
			type: Boolean,
			default: false
		}
	},

	/**
	 * Computed
	 */
	computed: {
		// Renamed from `component`: inside a <component :is="..."> tag, Vue 3
		// resolves the identifier `component` to the dynamic-component
		// placeholder itself, so the button rendered itself forever and the
		// page died with "Maximum call stack size exceeded".
		tag() {
			if (this.to) return 'router-link';
			if (this.href) return 'a';

			return 'button';
		}
	}
};
</script>
